import { Button } from '@cloudflare/kumo';
import { Paperclip, X, ArrowClockwise } from '@phosphor-icons/react';
import { useRef } from 'react';
import { ErrorNotice } from '../common.tsx';
import type { AttachmentQueue } from '../use-chat-attachments.ts';

export function ChatAttachments({ queue }: { queue: AttachmentQueue }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="chat-attachments px-0 py-2 flex flex-wrap items-center gap-[6px_10px]">
      <input
        ref={input}
        aria-label="Attach source files"
        type="file"
        multiple
        hidden
        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.csv,.tsv,.json,.rtf,.odt,image/png,image/jpeg,image/webp,image/avif,image/gif,image/svg+xml"
        onChange={(event) => {
          queue.add(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
      <Button
        size="sm"
        shape="square"
        className="size-8"
        variant="ghost"
        type="button"
        aria-label="Attach files"
        title="Attach files"
        onClick={() => input.current?.click()}
      >
        <Paperclip size={18} />
      </Button>
      <ErrorNotice error={queue.error} />
      {queue.items.length ? (
        <ul
          aria-label="Attachments"
          className="[flex-basis:100%] flex flex-wrap gap-2 [&_li]:px-2.5 [&_li]:py-[7px] [&_li]:border [&_li]:border-kumo-line [&_li]:rounded-[7px] [&_li]:flex [&_li]:items-center [&_li]:gap-2 [&_li]:max-w-full [&_li_>_div]:flex [&_li_>_div]:flex-col [&_li_>_div]:min-w-0 [&_strong]:text-[12px] [&_strong]:wrap-anywhere [&_span]:text-[11px] [&_span]:text-kumo-subtle"
        >
          {queue.items.map((item) => (
            <li key={item.id}>
              <div>
                <strong>{item.name}</strong>
                <span>
                  {item.status === 'uploading'
                    ? 'Uploading…'
                    : item.status === 'ready'
                      ? 'Ready'
                      : 'Upload failed'}
                </span>
              </div>
              {item.status === 'failed' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Retry ${item.name}`}
                  type="button"
                  onClick={() => queue.retry(item.id)}
                >
                  <ArrowClockwise />
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Remove ${item.name}`}
                type="button"
                onClick={() => queue.remove(item.id)}
              >
                <X />
              </Button>
              {item.error ? <ErrorNotice error={item.error} /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
